// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import setPlaceOrderErrorMessage from "@bg/App/Handlers/OnePageCheckout/SetPlaceOrderErrorMessage";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import {
    getSpreedlyFrame,
    SpreedlyIFrame,
    SpreedlyIframeStyles,
    SpreedlyType,
} from "@insite/client-framework/Common/Hooks/useSpreedlyFrame";
import {
    IFrame,
    TokenEx as TokenExType,
    TokenExIframeStyles,
    TokenExPCIIframeConfig,
} from "@insite/client-framework/Common/Hooks/useTokenExFrame";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { isNonEmptyString } from "@insite/client-framework/Common/Utilities/StringUtilities";
import validateCreditCard from "@insite/client-framework/Common/Utilities/validateCreditCard";
import logger from "@insite/client-framework/Logger";
import { SpreedlyConfig, TokenExConfig } from "@insite/client-framework/Services/SettingsService";
import siteMessage from "@insite/client-framework/SiteMessage";
import setIsPlacingOrder from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsPlacingOrder";
import translate from "@insite/client-framework/Translate";
import {
    convertSpreedlyCardType,
    convertTokenExCardType,
} from "@insite/content-library/Widgets/SavedPayments/PaymentUtilities";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import QuestionCircle from "@insite/mobius/Icons/QuestionCircle";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import SpreedlyFrame, { SpreedlyFramePresentationProps } from "@insite/mobius/SpreedlyFrame";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import TokenExFrame, { TokenExFramePresentationProps } from "@insite/mobius/TokenExFrame";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { forwardRef, Ref, SyntheticEvent, useEffect, useImperativeHandle, useRef, useState } from "react";
import styled, { css } from "styled-components";

interface OwnProps {
    canSaveCard?: boolean;
    iframe?: "TokenEx" | "Paymetric" | "Square" | "Paystand" | "Spreedly";
    paymetricFrameRef?: React.Ref<HTMLIFrameElement>;
    securityCodeError?: string;
    availableCardTypes: { key: string; value: string }[];
    availableMonths: { key: string; value: number }[];
    availableYears: { key: number; value: number }[];
    extendedStyles?: CreditCardDetailsEntryStyles;
    onResetPaymentMethodClick?: () => void;
    error?: string;
    bankAccountLastFourDigits?: string;
    creditCardLastFourDigits?: string;
    isIframeLoaded?: boolean;
    variation?: "onePageCheckout";
    tokenExConfig?: TokenExConfig;
    onTokenExSessionExpired?: (token: string) => void;
    tokenExFrameStyleConfig?: TokenExIframeStyles;
    spreedlyConfig?: SpreedlyConfig;
    spreedlyFrameStyleConfig?: SpreedlyIframeStyles;
    updateCardIsTokenized?: (value: boolean) => void;
    updateMaskedCardNumber?: (value: string) => void;
    onSaveCardUpdate: (value: boolean) => void;
    onCardHolderNameUpdate: (value: string) => void;
    onCardNumberUpdate: (value: string) => void;
    onCardTypeUpdate: (value: string) => void;
    onExpirationMonthUpdate: (value: number) => void;
    onExpirationYearUpdate: (value: number) => void;
    onSecurityCodeUpdate: (value: string) => void;
}

export interface CreditCardDetailsEntryStyles {
    creditCardDetailsContainer?: GridContainerProps;
    creditCardDetailsHeadingGridItem?: GridItemProps;
    creditCardDetailsHeading?: TypographyPresentationProps;
    saveCardGridItem?: GridItemProps;
    saveCardCheckboxGroup?: CheckboxGroupComponentProps;
    saveCardCheckbox?: CheckboxPresentationProps;
    cardHolderNameGridItem?: GridItemProps;
    cardHolderNameText?: TextFieldPresentationProps;
    cardNumberGridItem?: GridItemProps;
    cardNumberTokenExFrameWrapper?: InjectableCss;
    cardNumberTokenExFrame?: TokenExFramePresentationProps;
    cardNumberSpreedlyFrameWrapper?: InjectableCss;
    cardNumberSpreedlyFrame?: SpreedlyFramePresentationProps;
    cardNumberText?: TextFieldPresentationProps;
    cardTypeGridItem?: GridItemProps;
    cardTypeTokenExLabel?: TypographyPresentationProps;
    cardTypeTokenExText?: TypographyPresentationProps;
    cardTypeSpreedlyLabel?: TypographyPresentationProps;
    cardTypeSpreedlyText?: TypographyPresentationProps;
    cardTypeSelect?: SelectPresentationProps;
    expirationDateGridItem?: GridItemProps;
    expirationDateContainer?: GridContainerProps;
    expirationMonthGridItem?: GridItemProps;
    expirationMonthGridItemOnePageCheckout?: GridItemProps;
    expirationMonthSelect?: SelectPresentationProps;
    expirationYearGridItem?: GridItemProps;
    expirationYearGridItemOnePageCheckout?: GridItemProps;
    expirationYearSelect?: SelectPresentationProps;
    securityCodeGridItem?: GridItemProps;
    securityCodeGridItemOnePageCheckout?: GridItemProps;
    securityCodeTokenExFrameWrapper?: InjectableCss;
    securityCodeTokenExFrame?: TokenExFramePresentationProps;
    securityCodeSpreedlyFrameWrapper?: InjectableCss;
    securityCodeSpreedlyFrame?: SpreedlyFramePresentationProps;
    securityCodeText?: TextFieldPresentationProps;
    securityCodeHelpModal?: ModalPresentationProps;
    securityCodeHelpModalImageWrapper?: InjectableCss;
    securityCodeHelpImage?: LazyImageProps;
    paymetricGridItem?: GridItemProps;
    paymetricIframe?: InjectableCss;
    squareGridItem?: GridItemProps;
    squareContainer?: InjectableCss;
    paystandCardContainer?: InjectableCss;
    paystandGridItem?: GridItemProps;
    paymentMethodButton?: ButtonPresentationProps;
    creditCardInfoText?: TypographyPresentationProps;
    paystandCardHeaderText?: TypographyPresentationProps;
    paystandCardText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    resetPaymentMethodGridItem?: GridItemProps;
    onePageCheckoutSecurityCodeClickable?: ClickableProps;
}

export const creditCardDetailsEntryStyles: CreditCardDetailsEntryStyles = {
    creditCardDetailsContainer: {
        gap: 16,
        css: css`
            padding-right: 1px;
        `,
    },
    creditCardDetailsHeadingGridItem: { width: 12 },
    creditCardDetailsHeading: { variant: "h5" },
    saveCardGridItem: { width: 12 },
    cardHolderNameGridItem: { width: 12 },
    cardNumberGridItem: { width: 12 },
    cardNumberTokenExFrameWrapper: {
        css: css`
            height: inherit;
        `,
    },
    cardNumberSpreedlyFrameWrapper: {
        css: css`
            height: inherit;
        `,
    },
    cardTypeGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            &:empty {
                display: none;
            }
        `,
    },
    cardTypeTokenExLabel: { weight: fontConfig.weight.semiBold },
    cardTypeSpreedlyLabel: { weight: fontConfig.weight.semiBold },
    expirationDateGridItem: { width: 12 },
    expirationDateContainer: { gap: 20 },
    expirationMonthGridItem: { width: 6 },
    expirationMonthGridItemOnePageCheckout: { width: 4 },
    expirationYearGridItem: { width: 6 },
    expirationYearGridItemOnePageCheckout: { width: 4 },
    securityCodeGridItem: { width: 12 },
    securityCodeGridItemOnePageCheckout: { width: 4 },
    securityCodeTokenExFrameWrapper: {
        css: css`
            height: inherit;
        `,
    },
    securityCodeSpreedlyFrameWrapper: {
        css: css`
            height: inherit;
        `,
    },
    securityCodeHelpModalImageWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    paymetricGridItem: {
        width: 12,
    },
    paymetricIframe: {
        css: css`
            width: 100%;
        `,
    },
    squareGridItem: {
        width: 12,
    },
    squareContainer: {
        css: css`
            width: 100%;
        `,
    },
    paystandCardContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    paystandGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    resetPaymentMethodGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    errorText: {
        color: "danger",
        weight: fontConfig.weight.semiBold,
    },
    paystandCardHeaderText: {
        weight: fontConfig.weight.semiBold,
    },
    onePageCheckoutSecurityCodeClickable: {
        as: "span",
        css: css`
            display: inline-flex;
        `,
    },
    securityCodeTokenExFrame: {
        errorProps: {
            size: fontConfig.fontSize.xSmall,
            weight: fontConfig.weight.regular,
            lineHeight: fontConfig.lineHeight.singleLinexSmall,
            css: css`
                margin-top: 5px;
            `,
        },
    },
};

const CardNumberTokenExFrameWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;
const SecurityCodeTokenExFrameWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;
const SecurityCodeImageWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const PaymetricIframe = styled.iframe<InjectableCss>`
    ${({ css }) => css}
`;

const SquareContainer = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const PaystandCardContainer = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const CardNumberSpreedlyFrameWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;
const SecurityCodeSpreedlyFrameWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

declare const TokenEx: TokenExType;
declare const Spreedly: SpreedlyType;
let spreedlyIFrame: SpreedlyIFrame | undefined;
let tokenExIframe: IFrame;

export interface CreditCardDetailsValidation {
    validate: () => boolean;
    tokenize: () => void;
}

const isMonthAndYearBeforeToday = (month: number, year: number) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    return year < currentYear || (year === currentYear && month < currentMonth + 1);
};

const CreditCardDetailsEntry = (
    {
        iframe,
        paymetricFrameRef,
        extendedStyles,
        canSaveCard,
        availableCardTypes,
        availableMonths,
        availableYears,
        onResetPaymentMethodClick,
        error,
        bankAccountLastFourDigits,
        creditCardLastFourDigits,
        isIframeLoaded,
        variation,
        tokenExConfig,
        onTokenExSessionExpired,
        tokenExFrameStyleConfig,
        spreedlyConfig,
        spreedlyFrameStyleConfig,
        onSaveCardUpdate,
        onCardHolderNameUpdate,
        onCardNumberUpdate,
        onCardTypeUpdate,
        onExpirationMonthUpdate,
        onExpirationYearUpdate,
        onSecurityCodeUpdate,
        updateCardIsTokenized,
        updateMaskedCardNumber,
    }: OwnProps,
    ref: Ref<CreditCardDetailsValidation>,
) => {
    const [isSecurityCodeModalOpen, setIsSecurityCodeModalOpen] = useState(false);
    const [isTokenExIframeLoaded, setIsTokenExIframeLoaded] = useState(false);
    const [styles] = React.useState(() => mergeToNew(creditCardDetailsEntryStyles, extendedStyles));
    const [saveCard, setSaveCard] = useState(false);
    const [cardHolderName, setCardHolderName] = useState("");
    const [cardNumber, setCardNumber] = useState("");
    const [cardType, setCardType] = useState("");
    const [possibleCardType, setPossibleCardType] = useState("");
    const [expirationMonth, setExpirationMonth] = useState(1);
    const [expirationYear, setExpirationYear] = useState(new Date().getFullYear());
    const [securityCode, setSecurityCode] = useState("");
    const [cardHolderNameError, setCardHolderNameError] = useState("");
    const [cardNumberError, setCardNumberError] = useState("");
    const [cardTypeError, setCardTypeError] = useState("");
    const [securityCodeError, setSecurityCodeError] = useState("");
    const [expirationError, setExpirationError] = useState("");
    const cardHolderNameRef = useRef<HTMLInputElement>(null);
    const cardNumberRef = useRef<HTMLInputElement>(null);
    const cardTypeRef = useRef<HTMLSelectElement>(null);
    const securityCodeRef = useRef<HTMLInputElement>(null);
    const expirationMonthRef = useRef<HTMLSelectElement>(null);
    const expirationYearRef = useRef<HTMLSelectElement>(null);
    const [spreedlyReady, setSpreedlyReady] = useState(false);

    const resolvedShouldUseTokenEx = iframe === "TokenEx";
    const resolvedShouldUseSpreedly = iframe === "Spreedly";

    // XNG-Changes
    const dispatch = useAppDispatch();
    const { isPlacingOrder } = useAppSelector(state => ({
        isPlacingOrder: state.pages.onePageCheckout.isPlacingOrder,
    }));

    useImperativeHandle(
        ref,
        () => ({
            validate,
            tokenize,
        }),
        [resolvedShouldUseTokenEx, cardHolderName, cardNumber, cardType, expirationMonth, expirationYear, securityCode],
    );

    const tokenize = () => {
        if (resolvedShouldUseTokenEx) {
            tokenExIframe?.tokenize();
        }

        if (resolvedShouldUseSpreedly && spreedlyIFrame?.isValid()) {
            spreedlyIFrame?.tokenize({ month: expirationMonth, year: expirationYear, full_name: cardHolderName });
        }
    };

    const validate = () => {
        if (resolvedShouldUseTokenEx) {
            tokenExIframe?.validate();
        }

        if (resolvedShouldUseSpreedly) {
            spreedlyIFrame?.validate();
        }

        const cardHolderNameValid = validateCardHolderName(cardHolderName);
        const cardNumberResult = validateCardNumber(cardNumber);
        const cardTypeValid = validateCardType(cardType);
        const cardExpired = validateCardExpiration(expirationMonth, expirationYear);
        const securityCodeResult = validateSecurityCode(securityCode);

        if (!cardHolderNameValid) {
            cardHolderNameRef.current?.focus();
        } else if (cardNumberResult.cardNumberEmpty || !cardNumberResult.cardNumberValid) {
            cardNumberRef.current?.focus();
        } else if (!cardTypeValid) {
            cardTypeRef.current?.focus();
        } else if (cardExpired) {
            const today = new Date();
            if (expirationYear < today.getFullYear()) {
                expirationYearRef.current?.focus();
            } else {
                expirationMonthRef.current?.focus();
            }
        } else if (securityCodeResult.securityCodeEmpty || !securityCodeResult.securityCodeValid) {
            securityCodeRef.current?.focus();
        }

        return (
            cardHolderNameValid &&
            !cardNumberResult.cardNumberEmpty &&
            cardNumberResult.cardNumberValid &&
            cardTypeValid &&
            !cardExpired &&
            !securityCodeResult.securityCodeEmpty &&
            securityCodeResult.securityCodeValid
        );
    };

    const validateCardHolderName = (cardHolderName: string) => {
        const cardHolderNameValid = isNonEmptyString(cardHolderName);
        setCardHolderNameError(!cardHolderNameValid ? translate("Cardholder name is required.") : "");
        return cardHolderNameValid;
    };

    const validateCardNumber = (cardNumber: string) => {
        let cardNumberEmpty = false;
        let cardNumberValid = true;

        if (!resolvedShouldUseTokenEx && !resolvedShouldUseSpreedly) {
            cardNumberEmpty = !isNonEmptyString(cardNumber);
            cardNumberValid = validateCreditCard(cardNumber);
        }

        if (cardNumberEmpty) {
            setCardNumberError(translate("Credit card number is required."));
        } else if (!cardNumberValid) {
            setCardNumberError(translate("Credit card number is invalid."));
        } else {
            setCardNumberError("");
        }

        return { cardNumberEmpty, cardNumberValid };
    };

    const validateCardType = (cardType: string) => {
        const cardTypeValid =
            resolvedShouldUseTokenEx ||
            resolvedShouldUseSpreedly ||
            (!resolvedShouldUseTokenEx && isNonEmptyString(cardType));
        setCardTypeError(!cardTypeValid ? translate("Credit card type is required.") : "");
        return cardTypeValid;
    };

    const validateCardExpiration = (expirationMonth: number, expirationYear: number) => {
        const cardExpired = isMonthAndYearBeforeToday(expirationMonth, expirationYear);
        setExpirationError(cardExpired ? translate("Card is expired. Please enter a valid expiration date.") : "");
        return cardExpired;
    };

    const validateSecurityCode = (securityCode: string) => {
        let securityCodeEmpty = false;
        let securityCodeValid = true;

        if (!resolvedShouldUseTokenEx && !resolvedShouldUseSpreedly) {
            securityCodeEmpty = !isNonEmptyString(securityCode);
            securityCodeValid = /^\d+$/.test(securityCode);
        }

        if (securityCodeEmpty) {
            setSecurityCodeError(!securityCodeValid ? translate("Security code is required.") : "");
        } else if (!securityCodeValid) {
            setSecurityCodeError(translate("Security code is invalid."));
        } else {
            setSecurityCodeError("");
        }

        return { securityCodeEmpty, securityCodeValid };
    };
    const monthGridItemStyles =
        variation === "onePageCheckout"
            ? styles.expirationMonthGridItemOnePageCheckout
            : styles.expirationMonthGridItem;
    const yearGridItemStyles =
        variation === "onePageCheckout" ? styles.expirationYearGridItemOnePageCheckout : styles.expirationYearGridItem;
    const securityCodeGridItemStyles =
        variation === "onePageCheckout" ? styles.securityCodeGridItemOnePageCheckout : styles.securityCodeGridItem;

    const tokenExReinitTimeout = 500;
    let lastTokenExReinitTime = 0;
    const tokenExErrorsLimit = (20 * 1e3) / tokenExReinitTimeout;
    let currentTokenExErrorCount = 0;

    useEffect(() => {
        if (typeof TokenEx === "undefined" || !tokenExConfig) {
            return;
        }

        setUpTokenEx();
    }, [tokenExConfig, typeof TokenEx]);

    // XNG-Changes
    useEffect(() => {
        if (isPlacingOrder && resolvedShouldUseTokenEx && !isTokenExIframeLoaded) {
            dispatch(setPlaceOrderErrorMessage({ errorMessage: translate("Invalid Card") }));
            dispatch(setIsPlacingOrder({ isPlacingOrder: false }));
        }
    }, [isPlacingOrder]);

    const setUpTokenEx = () => {
        if (!tokenExConfig) {
            return;
        }
        currentTokenExErrorCount = 0;
        setUpTokenExIFrame(tokenExConfig);
    };

    const setUpTokenExIFrame = (config: TokenExConfig) => {
        if (tokenExIframe) {
            tokenExIframe.remove();
        }

        const iframeConfig: TokenExPCIIframeConfig & { font?: string } = {
            authenticationKey: config.authenticationKey,
            cvv: true,
            cvvContainerID: "tokenExSecurityCode",
            cvvInputType: "text",
            enablePrettyFormat: true,
            inputType: "text",
            origin: config.origin,
            pci: true,
            font: fontConfig.fontFamily.split(",")[0].trim(),
            styles: tokenExFrameStyleConfig,
            timestamp: config.timestamp,
            tokenExID: config.tokenExId,
            tokenScheme: config.tokenScheme,
            enableAriaRequired: true,
            title: translate("Credit card information"),
            customDataLabel: translate("credit card number"),
        };

        tokenExIframe = new TokenEx.Iframe("tokenExCardNumber", iframeConfig);
        tokenExIframe.load();
        tokenExIframe.on("load", _ => setIsTokenExIframeLoaded(true));
        tokenExIframe.on("tokenize", data => {
            onCardNumberUpdate(data.token);
            onSecurityCodeUpdate("CVV");
            onCardTypeUpdate(convertTokenExCardType(data.cardType));
            updateCardIsTokenized?.(true);
        });
        tokenExIframe.on("cardTypeChange", data => updatePossibleCardType(data.possibleCardType));
        tokenExIframe.on("validate", data => {
            if (data.isValid) {
                setCardNumberError("");
            } else {
                // XNG-Changes
                dispatch(setIsPlacingOrder({ isPlacingOrder: false }));
                if (data.validator === "required") {
                    setCardNumberError(translate("Credit card number is required."));
                }
                if (data.validator === "format") {
                    setCardNumberError(translate("Credit card number is invalid."));
                }
                // XNG-Changes
                else {
                    setSecurityCodeError(translate("Invalid Card"));
                }
            }

            if (data.isCvvValid) {
                setSecurityCodeError("");
            } else {
                // XNG-Changes
                dispatch(setIsPlacingOrder({ isPlacingOrder: false }));
                if (data.cvvValidator === "required") {
                    setSecurityCodeError(translate("Security code is required."));
                }
                if (data.cvvValidator === "format") {
                    setSecurityCodeError(translate("Security code is invalid."));
                }
            }
        });
        tokenExIframe.on("error", data => {
            logger.error(data);
            // <XNG-Changes>
            if (data.error.indexOf("Session expired") > -1) {
                setCardNumberError(siteMessage("Checkout_PaymentSessionExpired") as string);
                onTokenExSessionExpired?.(config.token || "");
                return;
            }

            dispatch(setIsPlacingOrder({ isPlacingOrder: false }));
            setSecurityCodeError(translate("Invalid Card"));
            // </XNG-Changes>
            if (
                Date.now() - lastTokenExReinitTime > tokenExReinitTimeout &&
                ++currentTokenExErrorCount <= tokenExErrorsLimit
            ) {
                lastTokenExReinitTime = Date.now();
                setTimeout(() => {
                    setUpTokenExIFrame(config);
                }, tokenExReinitTimeout);
            } else {
                setCardNumberError(translate("Something went wrong, please contact support"));
            }
        });
    };

    useEffect(() => {
        if (typeof Spreedly === "undefined" || !spreedlyConfig) {
            return;
        }
        spreedlyIFrame = getSpreedlyFrame();
        spreedlyIFrame.setup(
            spreedlyConfig.environmentKey,
            "spreedlyCardNumber",
            "spreedlySecurityCode",
            spreedlyFrameStyleConfig,
            () => {
                setSpreedlyReady(true);
            },
        );

        spreedlyIFrame.onTokenize((token, tokenData) => {
            setCardNumberError("");
            onCardNumberUpdate(token);
            onSecurityCodeUpdate("CVV");
            onCardTypeUpdate(convertSpreedlyCardType(tokenData.card_type));
            updateMaskedCardNumber?.(`-${tokenData.last_four_digits}`);
            updateCardIsTokenized?.(true);
        });
        spreedlyIFrame.onCardTypeChanged(possibleCardType =>
            updatePossibleCardType(convertSpreedlyCardType(possibleCardType)),
        );
        spreedlyIFrame.onError(message => {
            setCardNumberError(message);
        });
        spreedlyIFrame.onValidation(data => {
            if (data.validNumber) {
                setCardNumberError("");
            } else if (data.numberLength === 0) {
                setCardNumberError(translate("Credit card number is required."));
            } else {
                setCardNumberError(translate("Credit card number is invalid."));
            }

            if (data.validCvv) {
                setSecurityCodeError("");
            } else if (data.cvvLength === 0) {
                setSecurityCodeError(translate("Security code is required."));
            } else {
                setSecurityCodeError(translate("Security code is invalid."));
            }
        });
    }, [spreedlyConfig, typeof Spreedly]);

    const handleSecurityCodeHelpLinkClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        setIsSecurityCodeModalOpen(true);
    };

    const paymentMethodClickHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        onResetPaymentMethodClick?.();
    };

    const handleSecurityCodeModalClose = (_: React.SyntheticEvent<Element, Event>) => setIsSecurityCodeModalOpen(false);

    const updateSaveCard = (value: boolean) => {
        setSaveCard(value);
        onSaveCardUpdate(value);
    };
    const updateCardHolderName = (value: string) => {
        setCardHolderName(value);
        onCardHolderNameUpdate(value);
        validateCardHolderName(value);
    };
    const updateCardNumber = (value: string) => {
        setCardNumber(value);
        onCardNumberUpdate(value);
        validateCardNumber(value);
    };
    const updateCardType = (value: string) => {
        setCardType(value);
        onCardTypeUpdate(value);
        validateCardType(value);
    };
    const updatePossibleCardType = (value: string) => {
        setPossibleCardType(value);
        onCardTypeUpdate(value);
    };
    const updateExpirationMonth = (value: number) => {
        setExpirationMonth(value);
        onExpirationMonthUpdate(value);
    };
    const updateExpirationYear = (value: number) => {
        setExpirationYear(value);
        onExpirationYearUpdate(value);
    };
    const updateSecurityCode = (value: string) => {
        setSecurityCode(value);
        onSecurityCodeUpdate(value);
        validateSecurityCode(value);
    };
    const onSaveCardChange = (_: React.SyntheticEvent<Element, Event>, value: boolean) => updateSaveCard(value);
    const onCardHolderNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateCardHolderName(event.currentTarget.value);
    };
    const onCardNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateCardNumber(event.currentTarget.value);
    };
    const onCardTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        updateCardType(event.currentTarget.value);
    };
    const onExpirationMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const month = Number(event.currentTarget.value);
        if (Number.isNaN(month)) {
            return;
        }
        updateExpirationMonth(month);
    };
    const onExpirationYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const year = Number(event.currentTarget.value);
        if (Number.isNaN(year)) {
            return;
        }
        updateExpirationYear(year);
    };
    const onSecurityCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateSecurityCode(event.currentTarget.value);
    };

    // Xng-Change change securityCodeLabel varaible name to securityCodeHelpLabel and add infoIcon styles
    const securityCodeHelpLabel = translate("Locate my card's security code");
    const InfoIcon: {
        [key: string]: {
            src: string;
            color: string;
        };
    } = {
        bg: {
            src: "Info",
            color: "secondary.main",
        },
        bp: {
            src: "Info",
            color: "secondary.main",
        },
        bgs: {
            src: "Info2",
            color: "tertiary.main",
        },
    };
    const iconProps = InfoIcon[BLUEPRINT_NAME.toLowerCase()];
    const securityCodeHint = (
        <Link onClick={handleSecurityCodeHelpLinkClick} className="cvc-icon">
            <VisuallyHidden>{securityCodeHelpLabel}</VisuallyHidden>
            <Icon {...iconProps} size={15}></Icon>
        </Link>
    );

    const enterButtonLabel = cardNumber.length > 0 ? "Re-Enter Payment Details" : "Enter Payment Details";

    const securityCodeRequiredLabel =
        variation === "onePageCheckout" ? (
            <Clickable
                {...styles.onePageCheckoutSecurityCodeClickable}
                onClick={() => {
                    setIsSecurityCodeModalOpen(true);
                }}
            >
                <span>&nbsp;*&nbsp;</span>
                <QuestionCircle size={16} />
            </Clickable>
        ) : undefined;

    const securityCodeInput = resolvedShouldUseTokenEx ? (
        <TokenExFrame
            {...styles.securityCodeTokenExFrame}
            label={translate("Security code")}
            hint={securityCodeHint}
            tokenExIFrameContainer={
                <SecurityCodeTokenExFrameWrapper {...styles.securityCodeTokenExFrameWrapper} id="tokenExSecurityCode" />
            }
            disabled={!isTokenExIframeLoaded}
            required
            error={securityCodeError}
            data-test-selector="checkoutReviewAndSubmit_securityCode"
            requiredLabel=""
        />
    ) : resolvedShouldUseSpreedly ? (
        <SpreedlyFrame
            {...styles.securityCodeSpreedlyFrame}
            label={translate("Security code")}
            hint={securityCodeHint}
            spreedlyIFrameContainer={
                <SecurityCodeSpreedlyFrameWrapper
                    {...styles.securityCodeSpreedlyFrameWrapper}
                    id="spreedlySecurityCode"
                />
            }
            disabled={!spreedlyReady}
            required
            error={securityCodeError}
            data-test-selector="checkoutReviewAndSubmit_securityCode"
            requiredLabel=""
        />
    ) : (
        <TextField
            {...styles.securityCodeText}
            label={translate("Security code")}
            hint={variation === "onePageCheckout" ? undefined : securityCodeHint}
            value={securityCode}
            onChange={onSecurityCodeChange}
            required
            minLength={3}
            maxLength={4}
            error={securityCodeError}
            data-test-selector="checkoutReviewAndSubmit_securityCode"
            ref={securityCodeRef}
            requiredLabel=""
        />
    );

    return (
        <GridContainer {...styles.creditCardDetailsContainer}>
            {/* XNG-Change */}
            {/* show creditCardDetailsHeading for onePageCheckout */}
            {/* {!variation && ()} */}
            <GridItem {...styles.creditCardDetailsHeadingGridItem}>
                <Typography {...styles.creditCardDetailsHeading}>{translate("Credit Card Details")}</Typography>
            </GridItem>
            {/* Xng-Change */}
            {/* {canSaveCard && !variation && (
                <GridItem {...styles.saveCardGridItem}>
                    <CheckboxGroup {...styles.saveCardCheckboxGroup}>
                        <Checkbox {...styles.saveCardCheckbox} checked={saveCard} onChange={onSaveCardChange}>
                            {translate("Save card information")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
            )} */}
            {iframe === "Paymetric" ? (
                <GridItem {...styles.paymetricGridItem}>
                    <PaymetricIframe id="paymetricIframe" ref={paymetricFrameRef} {...styles.paymetricIframe} />
                </GridItem>
            ) : iframe === "Square" ? (
                <GridItem {...styles.squareGridItem}>
                    <SquareContainer id="square-container" {...styles.squareContainer} />
                </GridItem>
            ) : iframe === "Paystand" ? (
                <GridItem {...styles.paystandGridItem}>
                    {cardNumber.length > 0 && (
                        <PaystandCardContainer {...styles.paystandCardContainer}>
                            <Typography {...styles.paystandCardHeaderText}>
                                {translate("Payment Information")}
                            </Typography>
                            <Typography {...styles.paystandCardText}>{cardHolderName}</Typography>
                            <Typography {...styles.paystandCardText}>
                                {cardType} (...{creditCardLastFourDigits ?? bankAccountLastFourDigits})
                            </Typography>
                            {expirationMonth > 0 && expirationYear > 0 && (
                                <Typography {...styles.paystandCardText}>
                                    {expirationMonth}/{expirationYear}
                                </Typography>
                            )}
                        </PaystandCardContainer>
                    )}
                    <Button
                        className="ps-button"
                        {...styles.paymentMethodButton}
                        onClick={paymentMethodClickHandler}
                        disabled={!isIframeLoaded}
                    >
                        {translate(enterButtonLabel)}
                    </Button>
                    {error && <Typography {...styles.errorText}>{error}</Typography>}
                </GridItem>
            ) : (
                <>
                    {/* Xng-Change change structure */}
                    <GridItem {...styles.cardNumberGridItem}>
                        {resolvedShouldUseTokenEx ? (
                            <TokenExFrame
                                {...styles.cardNumberTokenExFrame}
                                // Xng-Change change label
                                label={translate("Credit Card Number")}
                                tokenExIFrameContainer={
                                    <CardNumberTokenExFrameWrapper
                                        {...styles.cardNumberTokenExFrameWrapper}
                                        id="tokenExCardNumber"
                                    />
                                }
                                disabled={!isTokenExIframeLoaded}
                                required
                                error={cardNumberError}
                                data-test-selector="checkoutReviewAndSubmit_cardNumber"
                                // Xng-Change
                                placeholder={translate("xxxx xxxx xxxx xxxx")}
                            />
                        ) : resolvedShouldUseSpreedly ? (
                            <SpreedlyFrame
                                {...styles.cardNumberSpreedlyFrame}
                                label={translate("Card Number")}
                                spreedlyIFrameContainer={
                                    <CardNumberSpreedlyFrameWrapper
                                        {...styles.cardNumberSpreedlyFrameWrapper}
                                        id="spreedlyCardNumber"
                                    />
                                }
                                disabled={!spreedlyReady}
                                required
                                error={cardNumberError}
                                data-test-selector="checkoutReviewAndSubmit_cardNumber"
                            />
                        ) : (
                            <TextField
                                {...styles.cardNumberText}
                                label={translate("Card Number")}
                                value={cardNumber}
                                onChange={onCardNumberChange}
                                required
                                maxLength={16}
                                error={cardNumberError}
                                data-test-selector="checkoutReviewAndSubmit_cardNumber"
                                ref={cardNumberRef}
                                // Xng-Change
                                placeholder={translate("xxxx xxxx xxxx xxxx")}
                            />
                        )}
                    </GridItem>
                    <GridItem {...styles.cardTypeGridItem}>
                        {!variation && resolvedShouldUseTokenEx ? (
                            <>
                                {/* <Typography {...styles.cardTypeTokenExLabel} id="cardTypeTokenEx">
                                    {translate("Card Type")}
                                </Typography>
                                <TokenExCardTypeDisplay
                                    {...styles.cardTypeTokenExText}
                                    possibleCardType={possibleCardType}
                                    aria-labelledby="cardTypeTokenEx"
                                /> */}
                            </>
                        ) : !variation && resolvedShouldUseSpreedly ? (
                            <>
                                <Typography {...styles.cardTypeSpreedlyLabel} id="cardTypeSpreedly">
                                    {translate("Card Type")}
                                </Typography>
                                <SpreedlyCardTypeDisplay
                                    {...styles.cardTypeSpreedlyText}
                                    possibleCardType={possibleCardType}
                                    aria-labelledby="cardTypeSpreedly"
                                />
                            </>
                        ) : (
                            <>
                                {/* <Select */}
                                {/*    {...styles.cardTypeSelect} */}
                                {/*    label={translate("Card Type")} */}
                                {/*    value={cardType} */}
                                {/*    onChange={onCardTypeChange} */}
                                {/*    required */}
                                {/*    error={cardTypeError} */}
                                {/*    data-test-selector="checkoutReviewAndSubmit_cardType" */}
                                {/*    ref={cardTypeRef} */}
                                {/* > */}
                                {/*    <option value="">{translate("Select a Payment Method")}</option> */}
                                {/*    {availableCardTypes.map(ct => ( */}
                                {/*        <option key={ct.value} value={ct.value}> */}
                                {/*            {ct.key} */}
                                {/*        </option> */}
                                {/*    ))} */}
                                {/* </Select> */}
                            </>
                        )}
                    </GridItem>
                    <GridItem {...styles.cardHolderNameGridItem}>
                        <TextField
                            {...styles.cardHolderNameText}
                            label={translate("Name on Card")}
                            value={cardHolderName}
                            onChange={onCardHolderNameChange}
                            required
                            maxLength={30}
                            error={cardHolderNameError}
                            data-test-selector="checkoutReviewAndSubmit_cardHolderName"
                            placeholder={translate("First and Last Name")}
                        />
                    </GridItem>
                    <GridItem {...styles.expirationDateGridItem}>
                        <GridContainer {...styles.expirationDateContainer}>
                            <GridItem {...monthGridItemStyles}>
                                <Select
                                    {...styles.expirationMonthSelect}
                                    label={translate("Expiration Month")}
                                    value={expirationMonth}
                                    onChange={onExpirationMonthChange}
                                    required
                                    error={expirationError}
                                    data-test-selector="checkoutReviewAndSubmit_expirationMonth"
                                    ref={expirationMonthRef}
                                >
                                    {availableMonths.map(month => (
                                        <option key={month.value} value={month.value}>
                                            {month.key}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                            <GridItem {...yearGridItemStyles}>
                                <Select
                                    {...styles.expirationYearSelect}
                                    label={translate("Expiration Year")}
                                    value={expirationYear}
                                    onChange={onExpirationYearChange}
                                    required
                                    data-test-selector="checkoutReviewAndSubmit_expirationYear"
                                    ref={expirationYearRef}
                                >
                                    {availableYears.map(year => (
                                        <option key={year.value} value={year.value}>
                                            {year.key}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                            {/* {variation === "onePageCheckout" && (
                                <GridItem {...securityCodeGridItemStyles}>{securityCodeInput}</GridItem>
                            )} */}
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.securityCodeGridItem}>
                        <StyledWrapper
                            css={css`
                                width: 100%;
                                position: relative;

                                .cvc-icon {
                                    position: absolute;
                                    top: 12px;
                                    right: -22px;
                                }
                            `}
                        >
                            {securityCodeInput}
                        </StyledWrapper>
                    </GridItem>
                    <Modal
                        headline={securityCodeHelpLabel}
                        isOpen={isSecurityCodeModalOpen}
                        handleClose={handleSecurityCodeModalClose}
                        {...styles.securityCodeHelpModal}
                    >
                        <SecurityCodeImageWrapper {...styles.securityCodeHelpModalImageWrapper}>
                            <LazyImage
                                src="/images/security_code_sample.jpg"
                                imgProps={{ width: 660, height: 264 }}
                                cloudinary={false}
                                altText={translate("Location of security code on card")}
                                {...styles.securityCodeHelpImage}
                            />
                        </SecurityCodeImageWrapper>
                    </Modal>
                </>
            )}
            {canSaveCard && !variation && (
                <GridItem {...styles.saveCardGridItem}>
                    <CheckboxGroup {...styles.saveCardCheckboxGroup}>
                        <Checkbox {...styles.saveCardCheckbox} checked={saveCard} onChange={onSaveCardChange}>
                            {translate("Save card information")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
            )}
        </GridContainer>
    );
};

type TokenExCardTypeDisplayProps = TypographyProps & {
    possibleCardType: string;
};

const TokenExCardTypeDisplay = ({ possibleCardType, ...otherProps }: TokenExCardTypeDisplayProps) => {
    let cardTypeDisplay = "";
    if (possibleCardType === "masterCard") {
        cardTypeDisplay = "Mastercard";
    } else if (possibleCardType === "americanExpress") {
        cardTypeDisplay = "American Express";
    } else if (possibleCardType === "discover") {
        cardTypeDisplay = "Discover";
    } else if (possibleCardType === "visa") {
        cardTypeDisplay = "Visa";
    }
    return <Typography {...otherProps}>{cardTypeDisplay}</Typography>;
};

const SpreedlyCardTypeDisplay = ({ possibleCardType, ...otherProps }: TokenExCardTypeDisplayProps) => {
    return <Typography {...otherProps}>{convertSpreedlyCardType(possibleCardType)}</Typography>;
};

export default forwardRef(CreditCardDetailsEntry);
